import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FolderSimple, PencilSimple, Trash, Plus, Funnel, X } from "@phosphor-icons/react";

export default function RecordOrganizer({
  recordType,
  items,
  setItems,
  numberField,
  dateField = "date",
  children,
}) {
  const [categories, setCategories] = useState([]);
  const [newName, setNewName] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [numberFilter, setNumberFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [targetCategory, setTargetCategory] = useState("");

  const loadCategories = async () => {
    const r = await api.get(`/record-categories?record_type=${recordType}`);
    setCategories(r.data || []);
  };
  useEffect(() => { loadCategories(); }, [recordType]);

  const filteredItems = useMemo(() => {
    const q = numberFilter.trim().toLowerCase();
    return (items || []).filter((row) => {
      if (categoryFilter === "uncategorized" && row.category_id) return false;
      if (categoryFilter !== "all" && categoryFilter !== "uncategorized" && row.category_id !== categoryFilter) return false;
      if (q && !String(row[numberField] || "").toLowerCase().includes(q)) return false;
      const d = String(row[dateField] || "").slice(0, 10);
      if (dateFrom && (!d || d < dateFrom)) return false;
      if (dateTo && (!d || d > dateTo)) return false;
      return true;
    });
  }, [items, categoryFilter, numberFilter, dateFrom, dateTo, numberField, dateField]);

  const visibleIds = filteredItems.map((x) => x.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    return next;
  });

  const createCategory = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post("/record-categories", { record_type: recordType, name });
      setNewName("");
      await loadCategories();
      toast.success("Kategori oluşturuldu");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const renameCategory = async (cat) => {
    const name = window.prompt("Kategori adı", cat.name);
    if (!name || !name.trim() || name.trim() === cat.name) return;
    try {
      await api.put(`/record-categories/${cat.id}`, { name: name.trim() });
      await loadCategories();
      toast.success("Kategori adı güncellendi");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`“${cat.name}” kategorisi silinsin mi? İçindeki kayıtlar silinmez, kategorisiz kalır.`)) return;
    try {
      await api.delete(`/record-categories/${cat.id}`);
      setItems((prev) => prev.map((x) => x.category_id === cat.id ? { ...x, category_id: null } : x));
      if (categoryFilter === cat.id) setCategoryFilter("all");
      await loadCategories();
      toast.success("Kategori silindi; kayıtlar korundu");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const assignSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return toast.error("Önce en az bir kayıt seçin");
    const categoryId = targetCategory === "uncategorized" ? null : targetCategory;
    if (targetCategory === "") return toast.error("Hedef kategori seçin");
    try {
      await api.post("/record-categories/assign", {
        record_type: recordType,
        record_ids: ids,
        category_id: categoryId,
      });
      setItems((prev) => prev.map((x) => selected.has(x.id) ? { ...x, category_id: categoryId } : x));
      setSelected(new Set());
      toast.success(`${ids.length} kayıt kategoriye taşındı`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const countFor = (id) => (items || []).filter((x) => id === "uncategorized" ? !x.category_id : x.category_id === id).length;
  const clearFilters = () => { setCategoryFilter("all"); setNumberFilter(""); setDateFrom(""); setDateTo(""); };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 p-4 space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2"><FolderSimple size={16}/> Kategori Listeleri</div>
            <div className="text-xs text-slate-400 mt-1">Kayıtları ülke, müşteri veya sevkiyat grubuna göre arşivleyin.</div>
          </div>
          <div className="flex gap-2 w-full lg:w-auto">
            <input className="border border-slate-300 px-3 py-2 text-sm flex-1 lg:w-56 outline-none focus:border-blue-500" placeholder="Yeni kategori adı" value={newName} onChange={(e)=>setNewName(e.target.value)} onKeyDown={(e)=>e.key === "Enter" && createCategory()} />
            <button onClick={createCategory} className="inline-flex items-center gap-1.5 bg-[#2563EB] text-white px-3 py-2 text-sm font-semibold"><Plus size={14}/> Ekle</button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={()=>setCategoryFilter("all")} className={`px-3 py-1.5 text-xs border font-semibold ${categoryFilter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"}`}>Tümü ({items.length})</button>
          <button onClick={()=>setCategoryFilter("uncategorized")} className={`px-3 py-1.5 text-xs border font-semibold ${categoryFilter === "uncategorized" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"}`}>Kategorisiz ({countFor("uncategorized")})</button>
          {categories.map((cat)=><div key={cat.id} className={`inline-flex items-center border ${categoryFilter === cat.id ? "border-blue-600 bg-blue-50" : "border-slate-300 bg-white"}`}>
            <button onClick={()=>setCategoryFilter(cat.id)} className="px-3 py-1.5 text-xs font-semibold">{cat.name} ({countFor(cat.id)})</button>
            <button onClick={()=>renameCategory(cat)} className="p-1.5 text-slate-400 hover:text-blue-600" title="Adını düzenle"><PencilSimple size={13}/></button>
            <button onClick={()=>deleteCategory(cat)} className="p-1.5 text-slate-400 hover:text-red-600" title="Kategoriyi sil"><Trash size={13}/></button>
          </div>)}
        </div>
      </div>

      <div className="bg-white border border-slate-200 p-4 flex flex-col xl:flex-row gap-3 xl:items-end justify-between">
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">No ile filtrele
            <input className="block mt-1 border border-slate-300 px-3 py-2 text-sm w-52" placeholder={recordType === "plan" ? "PLN-..." : "INV-..."} value={numberFilter} onChange={(e)=>setNumberFilter(e.target.value)} />
          </label>
          <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Başlangıç Tarihi
            <input type="date" className="block mt-1 border border-slate-300 px-3 py-2 text-sm" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} />
          </label>
          <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Bitiş Tarihi
            <input type="date" className="block mt-1 border border-slate-300 px-3 py-2 text-sm" value={dateTo} onChange={(e)=>setDateTo(e.target.value)} />
          </label>
          <button onClick={clearFilters} className="inline-flex items-center gap-1 border border-slate-300 px-3 py-2 text-sm h-[38px]"><X size={13}/> Filtreyi Temizle</button>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="text-xs font-semibold text-slate-600 self-center mr-1">{selected.size} seçili</div>
          <select className="border border-slate-300 px-3 py-2 text-sm min-w-52" value={targetCategory} onChange={(e)=>setTargetCategory(e.target.value)}>
            <option value="">Kategori seç...</option>
            <option value="uncategorized">Kategorisiz yap</option>
            {categories.map((c)=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={assignSelected} disabled={!selected.size} className="bg-slate-900 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-semibold">Seçilenleri Taşı</button>
        </div>
      </div>

      {children({ filteredItems, selected, toggle, toggleAll, allVisibleSelected, categories })}
    </div>
  );
}
